/**
 * Portal catalog — destinations for the side PortalSelector.
 * Add a destination here; the rail picks it up (same pattern as themeCatalog).
 *
 * Boundaries (doctrine):
 *   Production / employee identity → .com
 *   Dev / sandbox → .net
 * Never blur hosts in labels or deep-links.
 */

export type PortalEnv = 'production' | 'sandbox' | 'local' | 'unknown'
export type PortalKind = 'client' | 'employee' | 'status' | 'lab' | 'ops'
export type PortalBadge = 'PROD' | 'DEV' | 'LAB' | 'EMP' | 'OPS' | 'HERE'

export interface PortalDestination {
  id: string
  label: string
  description: string
  env: Exclude<PortalEnv, 'unknown' | 'local'> | 'local'
  kind: PortalKind
  badge: PortalBadge
  /**
   * Absolute origin including protocol, no trailing slash.
   * null = same origin (path-only navigation).
   */
  origin: string | null
  /** Path when not preserving current path; ignored if preservePath and path exists */
  path: string
  /** When true, keep window.location.pathname (+ search) on host switch */
  preservePath: boolean
  /** Highlight as primary one-click action */
  primary?: boolean
  /** Only list when NODE_ENV=development (labs) */
  devOnly?: boolean
}

export const PORTAL_CATALOG: PortalDestination[] = [
  {
    id: 'client-sandbox',
    label: 'Client desk · DEV',
    description: 'Sandbox trading portal (.net). Same path when possible.',
    env: 'sandbox',
    kind: 'client',
    badge: 'DEV',
    origin: 'https://sandbox.mechagora.net',
    path: '/client/dashboard',
    preservePath: true,
    primary: true,
  },
  {
    id: 'client-prod',
    label: 'Client desk · PROD',
    description: 'Production trading portal (.com). Confirm before live work.',
    env: 'production',
    kind: 'client',
    badge: 'PROD',
    origin: 'https://mechagora.com',
    path: '/client/dashboard',
    preservePath: true,
  },
  {
    id: 'employee-sandbox',
    label: 'Employee portal · DEV',
    description: 'Employee identity on sandbox (employee.sandbox). Separate from client.',
    env: 'sandbox',
    kind: 'employee',
    badge: 'EMP',
    origin: 'https://employee.sandbox.mechagora.net',
    path: '/',
    preservePath: false,
  },
  {
    id: 'settings-portal',
    label: 'Settings portal',
    description: 'Operator+ desk identity hub — Theme Studio & saved systems.',
    env: 'sandbox',
    kind: 'client',
    badge: 'DEV',
    origin: null,
    path: '/client/settings/portal',
    preservePath: false,
  },
  {
    id: 'theme-lab',
    label: 'Theme lab',
    description: 'Palette selector + live mini desk (DEV only route).',
    env: 'sandbox',
    kind: 'lab',
    badge: 'LAB',
    origin: null,
    path: '/client/dashboard/theme-lab',
    preservePath: false,
    devOnly: true,
  },
  {
    id: 'flagship-preview',
    label: 'Flagship preview',
    description: 'Mock desk chrome without live session (DEV only).',
    env: 'sandbox',
    kind: 'lab',
    badge: 'LAB',
    origin: null,
    path: '/client/dashboard/preview',
    preservePath: false,
    devOnly: true,
  },
  {
    id: 'status-sandbox',
    label: 'Status · DEV',
    description: 'Public status page on sandbox.',
    env: 'sandbox',
    kind: 'status',
    badge: 'OPS',
    origin: 'https://status.sandbox.mechagora.net',
    path: '/',
    preservePath: false,
  },
]

export function detectPortalEnv(hostname?: string): PortalEnv {
  if (typeof window === 'undefined' && !hostname) return 'unknown'
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase()
  if (!host) return 'unknown'
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) return 'local'
  if (host.includes('sandbox.mechagora.net') || host.endsWith('.mechagora.net')) return 'sandbox'
  if (host.includes('mechagora.com')) return 'production'
  return 'unknown'
}

export function isPortalHere(dest: PortalDestination, hostname?: string, pathname?: string): boolean {
  const host =
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')
  const env = detectPortalEnv(host)

  if (dest.origin === null) {
    // same-origin lab routes
    return path === dest.path || path.startsWith(`${dest.path}/`)
  }

  try {
    const destHost = new URL(dest.origin).hostname
    if (host !== destHost && host !== `www.${destHost}` && `www.${host}` !== destHost) {
      return false
    }
    // On matching host: "here" if client desk destinations match kind broadly
    if (dest.kind === 'client') {
      return env === dest.env || (env === 'local' && dest.env === 'sandbox')
    }
    if (dest.kind === 'employee') {
      return host.startsWith('employee.')
    }
    return true
  } catch {
    return false
  }
}

/** Build absolute URL for a destination */
export function buildPortalHref(
  dest: PortalDestination,
  opts?: { pathname?: string; search?: string }
): string {
  const pathname =
    opts?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : dest.path)
  const search =
    opts?.search ?? (typeof window !== 'undefined' ? window.location.search : '')

  let path = dest.path
  if (dest.preservePath && pathname) {
    // Keep app path; avoid carrying auth-only return loops to wrong IdP surface
    const safe =
      pathname.startsWith('/auth') || pathname.startsWith('/api')
        ? dest.path
        : pathname
    path = safe || dest.path
  }

  if (!dest.origin) {
    return `${path}${dest.preservePath ? search : ''}`
  }
  return `${dest.origin}${path}${dest.preservePath ? search : ''}`
}

export function listPortals(options?: { includeDevOnly?: boolean }): PortalDestination[] {
  const includeDev =
    options?.includeDevOnly ??
    (typeof process !== 'undefined' && process.env.NODE_ENV === 'development')
  return PORTAL_CATALOG.filter((p) => (p.devOnly ? includeDev : true))
}

export function getPrimaryDevPortal(): PortalDestination {
  return PORTAL_CATALOG.find((p) => p.primary) ?? PORTAL_CATALOG[0]!
}

export function envBadgeLabel(env: PortalEnv): string {
  switch (env) {
    case 'production':
      return 'PROD'
    case 'sandbox':
      return 'DEV'
    case 'local':
      return 'LOCAL'
    default:
      return 'ENV'
  }
}
